/**
 * useVideoSeo - 视频SEO结构化数据生成和注入
 * 
 * 功能：
 * 1. 为YouTube视频生成VideoObject Schema.org结构化数据
 * 2. 自动从embed URL提取视频ID
 * 3. 生成YouTube标准缩略图URL
 * 4. 通过useHead注入结构化数据到页面
 */

import { useHead } from '#app'

export interface YouTubeVideo {
    title: string
    embedUrl: string
    description?: string
    uploadDate?: string
    duration?: string
}

/**
 * 从YouTube embed URL提取视频ID
 * 支持格式：
 * - https://www.youtube.com/embed/VIDEO_ID
 * - https://youtube.com/embed/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID?params
 */
function extractYouTubeVideoId(embedUrl: string): string | null {
    try {
        const url = new URL(embedUrl)
        const pathname = url.pathname
        
        // 提取 /embed/ 后面的视频ID
        const match = pathname.match(/\/embed\/([^/?]+)/)
        if (match && match[1]) {
            return match[1]
        }
        
        return null
    } catch (error) {
        console.warn('Failed to extract YouTube video ID from URL:', embedUrl, error)
        return null
    }
}

/**
 * 生成YouTube标准缩略图URL
 * maxresdefault.jpg 是最高质量的缩略图
 */
function getYouTubeThumbnailUrl(videoId: string): string {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
}

/**
 * 生成YouTube视频的VideoObject结构化数据
 * 
 * 注意：Google Search Console 要求以下必填字段：
 * - name: 视频标题
 * - description: 视频描述（如果未提供，将使用 title 作为默认值）
 * - thumbnailUrl: 视频缩略图
 * - uploadDate: 上传日期（ISO 8601 格式，必须包含时区信息）
 */
function generateYouTubeVideoObject(video: YouTubeVideo): any {
    const videoId = extractYouTubeVideoId(video.embedUrl)
    
    if (!videoId) {
        console.warn('Could not extract video ID from embed URL:', video.embedUrl)
        return null
    }
    
    const videoObject: any = {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: video.title,
        // description 是 Google 必填字段，如果未提供则使用 title
        description: video.description || video.title,
        embedUrl: video.embedUrl,
        thumbnailUrl: getYouTubeThumbnailUrl(videoId),
        // uploadDate 是 Google 必填字段，必须包含时区信息
        // 如果未提供，使用默认日期（ISO 8601 格式）
        uploadDate: video.uploadDate || '2025-10-20T00:00:00+08:00',
    }
    
    if (video.duration) {
        videoObject.duration = video.duration
    }
    
    return videoObject
}

/**
 * 为多个YouTube视频生成结构化数据并注入到页面
 * 
 * @param videos YouTube视频数组
 * @param options 选项
 */
export function useVideoSeo(
    videos: YouTubeVideo[],
    options: {
        /** 是否只为第一个视频生成结构化数据（Google通常只需要主要视频） */
        primaryOnly?: boolean
    } = {}
) {
    if (!videos || videos.length === 0) {
        return
    }
    
    const { primaryOnly = false } = options
    
    // 生成结构化数据
    // 过滤掉无效的视频，确保类型安全
    const validVideos = videos.filter(
        (v): v is YouTubeVideo => 
            v !== undefined && 
            v !== null && 
            typeof v === 'object' && 
            'title' in v && 
            'embedUrl' in v &&
            typeof v.title === 'string' &&
            typeof v.embedUrl === 'string'
    ) as YouTubeVideo[]
    
    if (validVideos.length === 0) {
        console.warn('No valid videos provided')
        return
    }
    
    const videosToProcess: YouTubeVideo[] = primaryOnly 
        ? validVideos.slice(0, 1) 
        : validVideos
    const videoObjects = videosToProcess
        .map(generateYouTubeVideoObject)
        .filter((obj): obj is NonNullable<typeof obj> => obj !== null)
    
    if (videoObjects.length === 0) {
        console.warn('No valid video objects generated')
        return
    }
    
    // 根据Google最佳实践，多个视频应使用 @graph 格式
    // 如果只有一个视频，直接注入单个VideoObject
    // 如果有多个视频，使用 @graph 格式包含所有VideoObject
    let structuredData: any
    if (videoObjects.length === 1) {
        structuredData = videoObjects[0]
    } else if (videoObjects.length > 1) {
        // 使用 @graph 格式，这是Google推荐的多视频结构化数据格式
        structuredData = {
            '@context': 'https://schema.org',
            '@graph': videoObjects,
        }
    } else {
        return
    }
    
    // 注入到页面head
    // 注意：useHead 需要在 setup 阶段调用，不能在 onMounted 中调用
    useHead({
        script: [
            {
                type: 'application/ld+json',
                innerHTML: JSON.stringify(structuredData),
            },
        ],
    })
}

/**
 * 为单个YouTube视频生成结构化数据并注入到页面
 * 
 * @param video YouTube视频对象
 */
export function useSingleVideoSeo(video: YouTubeVideo) {
    useVideoSeo([video], { primaryOnly: true })
}
